import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsEvent, useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Input, Toast, Typography, useInput, Spinner } from '@chilecompra/react-kit/components'
import { formatDNI, checkInputErrorMaxLength, checkInputErrorDniFormat } from '@chilecompra/react-kit/utils'
import {
  isLocalEnv,
  REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED,
  REACT_APP_HELP_URL,
  REACT_APP_TAXES_KEY_SEED
} from '../../config/settings/environment'
import { useCsrfToken } from '../../modules/hooks/security'
import { checkIsNaturalPerson, checkInputErrorDni } from '../../modules/utils/helpers'
import { onSignInTaxesKeyThunk } from '../TaxesKeyAuth/TaxesKeyAuth.actions'
import { onCheckGuestBusinessThunk, onGetSellerBlockedInfoThunk } from './RegisterWizard.actions'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import {
  Root,
  Section,
  Strong,
  Title,
  Instructions,
  Form,
  Search,
  SearchClearIcon,
  SearchIcon,
  SearchLoadingIcon,
  TaxesKeyInstructions,
  TaxesKey,
  TaxesKeyButton,
  TaxesKeyHelp,
  TaxesKeyHelpIcon,
  TaxesKeyHelpLink,
  ActivitiesLink,
  LinkHelp,
  SpinnerContainer
} from './RegisterWizardBusinessSearch.styles'

/**
 * The RegisterWizardBusinessSearch's container.
 */
const RegisterWizardBusinessSearch = props => {
  const { onChangeStep } = props
  const errorMessage = 'Error: RUT inválido. Por favor, ingresa un formato correcto'
  const dispatch = useDispatch()
  const [clear, setClear] = useState(false)
  const { blockedInfo, business, errors, loading } = useSelector(state => state.registerWizard)
  const {
    value: dniValue,
    onChange: handleChange,
    setValue: setDNIValue,
    error: dniError
  } = useInput({
    initialValue: business.dni || '',
    changeCallback: () => setClear(false),
    formatCallbacks: [formatDNI],
    overwriteCallback: () => business.dni,
    errorCallbacks: [
      checkInputErrorMaxLength(12),
      checkInputErrorDniFormat(errorMessage),
      checkInputErrorDni(errorMessage)
    ]
  })

  const csrfToken = useCsrfToken(REACT_APP_TAXES_KEY_SEED)
  const makeSubmission = () => {
    setClear(true)
    dispatch(
      onGetSellerBlockedInfoThunk({
        dni: dniValue,
        checkGuestBusinessCallback: () => {
          dispatch(onCheckGuestBusinessThunk({ dni: dniValue, callback: () => onChangeStep('business-info') }))
        }
      })
    )
  }
  const [isNaturalPerson, setIsNaturalPerson] = useState(true)
  const handleClick = () => {
    setIsNaturalPerson(checkIsNaturalPerson(dniValue))
    if (!clear) {
      return makeSubmission()
    }

    setClear(false)
    setDNIValue('')
  }
  const handleSubmit = event => {
    event.preventDefault()
    if (dniValue === '' || loading || dniError) return
    makeSubmission()
  }
  const handleTaxesKey = () => {
    if (
      isLocalEnv ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED === false ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED === 'false'
    ) {
      onChangeStep('business-info')
    } else {
      dispatch(onSignInTaxesKeyThunk({ csrfToken, dni: dniValue }))
    }
  }
  const handleRecoveryByTaxesKey = () => {
    if (
      isLocalEnv ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED === false ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_ENABLED === 'false'
    ) {
      onChangeStep('recovery-company')
    } else {
      dispatch(onSignInTaxesKeyThunk({ csrfToken, dni: dniValue, isRecovery: true }))
    }
  }

  useAnalyticsEvent({ action: 'Finaliza registro de usuario', category: 'registrar-usuario' })
  useAnalyticsPageView('/paso/3/buscar_empresa')

  return (
    <Root>
      <Section item>
        <Title>Ahora asociaremos tu empresa a tu nuevo perfil</Title>
      </Section>
      <Section item margin="23px 0 22px" maxWidth="632px">
        <Instructions>
          Ingresa el RUT de tu empresa para registrarte como persona jurídica, o tu RUT personal para registrarte como
          persona natural.
        </Instructions>
      </Section>
      <Section item maxWidth="508px">
        <Form onSubmit={handleSubmit}>
          <Input
            label="rut de empresa o persona natural"
            suffix={
              <Search
                clear={clear && !loading}
                disabled={dniValue === '' || loading || dniError}
                type="button"
                onClick={handleClick}
              >
                {!clear && !loading && <SearchIcon />}
                {loading && <SearchLoadingIcon />}
                {clear && !loading && <SearchClearIcon />}
              </Search>
            }
            error={dniError}
            value={dniValue}
            onChange={handleChange}
          />
        </Form>
      </Section>
      <Section item margin={errors.businessSearchError ? '0 0 22px' : 'initial'} maxWidth="551px">
        {loading && (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        )}
        {errors.businessSearchError === 'SOMETHING_WENT_WRONG_ERROR' && !loading && (
          <Toast severity="warning" title="Error al obtener la información del RUT ingresado">
            <br />
            Lo sentimos, estamos presentando problemas para traer la información asociada al RUT ingresado. Por favor
            intenta de nuevo en unos minutos.
          </Toast>
        )}
        {!blockedInfo.isBlocked && errors.businessSearchError === 'DNI_BELONGS_TO_A_DECEASED_ERROR' && !loading && (
          <Toast severity="warning" title="RUT de Persona Fallecida">
            <br />
            El RUT ingresado corresponde a una persona fallecida, según los registros del Servicio de Registro Civil.
            Verifica la información e intenta nuevamente.
          </Toast>
        )}
        {!blockedInfo.isBlocked && errors.businessSearchError === 'DNI_BELONGS_TO_A_MINOR_ERROR' && !loading && (
          <Toast severity="warning" title="RUT de Persona Menor de Edad">
            <br />
            El RUT ingresado corresponde a una persona menor de edad, según los registros del Servicio de Registro
            Civil. Verifica la información e intenta nuevamente.
          </Toast>
        )}
        {!blockedInfo.isBlocked && errors.businessSearchError === 'DNI_IS_BLACKLISTED_ERROR' && !loading && (
          <Toast severity="warning" title="Empresa inhabilitada">
            <br />
            Empresa inhabilitada por encontrarse en la lista de personas naturales y jurídicas declaradas inelegibles
            para la adjudicación de contratos elaborada por el Banco interamericano de Desarrollo.
          </Toast>
        )}
        {!blockedInfo.isBlocked && errors.businessSearchError === 'DNI_DOES_NOT_MATCH_ERROR' && !loading && (
          <Toast severity="warning" title="RUT ingresado no coincide">
            <br />
            Hemos detectado que ingresaste un RUT de otra persona, verifica que lo estés ingresando correctamente e
            intenta de nuevo.
          </Toast>
        )}
        {!blockedInfo.isBlocked && errors.businessSearchError === 'DNI_ALREADY_LINKED_ERROR' && !loading && (
          <>
            <Toast severity="warning" width="475px" margin="0 0 15px 0" title="Empresa ya asociada a otro usuario">
              <br />
              La empresa no puede ser asociada a tu RUT.
            </Toast>
            <ProtectByFeatures rule={features => features['com.seller/recovery.admin']}>
              <Section item maxWidth="507px">
                {!isNaturalPerson && (
                  <Typography fontSize="13px" lineHeight="18px">
                    Si el o los administradores actuales ya no se encuentran disponibles, puedes&nbsp;
                    <TaxesKeyHelpLink onClick={handleRecoveryByTaxesKey}>
                      solicitar administrar esta empresa ingresando con Clave Tributaria.
                    </TaxesKeyHelpLink>
                  </Typography>
                )}

                {isNaturalPerson && (
                  <Typography fontSize="13px" lineHeight="18px">
                    Si requieres administrar esta empresa, <Strong> solicita al usuario con rol Administrador </Strong>
                    que lo realice en la plataforma ingresando a la pestaña Usuarios, desde el menú Mi Empresa.
                  </Typography>
                )}
              </Section>
            </ProtectByFeatures>
            <ProtectByFeatures rule={features => !features['com.seller/recovery.admin']}>
              <Toast severity="info" width="475px" title="Para recuperar la administración de tu empresa.">
                <br />
                Contáctanos en la <Strong>Mesa de Ayuda: 600 7000 600</Strong>
                <br />o <Strong>+56 44 236 0646.</Strong>
              </Toast>
            </ProtectByFeatures>
          </>
        )}
        {!blockedInfo.isBlocked && errors.businessSearchError === 'DNI_NOT_INITIALIZED_ERROR' && !loading && (
          <Toast severity="warning" title="RUT sin inicio de actividades">
            <br />
            Hemos detectado que este RUT no tiene{' '}
            <ActivitiesLink href="https://www.sii.cl/servicios_online/1031-1034.html" target="_blank">
              Inicio de Actividades
            </ActivitiesLink>{' '}
            en el Servicio de Impuestos Internos.
          </Toast>
        )}
        {!blockedInfo.isBlocked && errors.businessSearchError === 'DNI_DOES_NOT_MATCH_TAXES_KEY_ERROR' && !loading && (
          <Toast severity="warning" title="RUT de Clave Tributaria no coincide">
            <br />
            Hemos detectado que iniciaste sesión en Clave Tributaria con un RUT de persona jurídica distinto al
            ingresado en este paso. Verifica que cuentas con los datos correctos e intenta nuevamente.
          </Toast>
        )}
        {!blockedInfo.isBlocked &&
          errors.businessSearchError === 'DNI_DOES_NOT_MATCH_UNIQUE_PASS_ERROR' &&
          !loading && (
            <Toast severity="warning" title="RUT de Clave Única no coincide">
              <br />
              Hemos detectado que iniciaste sesión en Clave Única con un RUT de persona natural distinto al ingresado en
              este paso. Verifica que cuentas con los datos correctos e intenta nuevamente.
            </Toast>
          )}

        {!blockedInfo.loading && errors.businessSearchError !== 'SOMETHING_WENT_WRONG_ERROR' && (
          <>
            {blockedInfo.error && blockedInfo.error.reason !== 'DNI_INVALID_ERROR' && (
              <Toast severity="warning" textDirection="vertical" title="Información temporalmente no disponible">
                <br />
                En estos momentos no podemos mostrar esta información debido a una intermitencia en los servicios del
                Servicio de Impuestos Internos. Por favor, vuelve a intentarlo en unos minutos.
              </Toast>
            )}

            {blockedInfo.error && blockedInfo.error.reason === 'DNI_INVALID_ERROR' && (
              <Toast severity="warning" textDirection="vertical" title="RUT ingresado no es válido">
                Según los registros del Servicio de Registro Civil, el RUT ingresado no es válido. Verifica la
                información e intenta nuevamente.
              </Toast>
            )}

            {!blockedInfo.error && blockedInfo.isBlocked && (
              <Toast severity="warning" title="Empresa registrada y bloqueada en Mercado Público">
                <br />
                Nuestros registros indican que la empresa ya se encuentra registrada y está bloqueada en la plataforma
                de Mercado Público debido a: <Strong>{blockedInfo.reason}</Strong> Si necesitas más información,
                solicítala a través de nuestro{' '}
                <LinkHelp href={REACT_APP_HELP_URL} rel="noreferrer" target="_blank">
                  Centro de Ayuda.
                </LinkHelp>
              </Toast>
            )}
          </>
        )}
      </Section>

      {!blockedInfo.isBlocked && business?.entityType === 'legal' && !loading && (
        <>
          <Section item maxWidth="472px">
            <TaxesKeyInstructions>
              Hemos detectado que este RUT corresponde a la empresa <Strong>{business?.name}</Strong>.
              <br />
              Ahora verificaremos que tienes autorización para representar a esta empresa en nuestra plataforma, para
              esto deberás ingresar tu <Strong>Clave Tributaria</Strong> haciendo click en el siguiente botón:
            </TaxesKeyInstructions>
          </Section>
          <Section>
            <TaxesKey>
              <TaxesKeyButton
                alt="Clave Tributaria"
                src="/assets/images/clave_tributaria.png"
                srcset="/assets/images/clave_tributaria@2x.png 2x"
                onClick={handleTaxesKey}
              />
              <TaxesKeyHelp>
                <TaxesKeyHelpLink
                  href="http://www.sii.cl/destacados/tramites/clavetributaria.html#:~:text=%C2%BFQu%C3%A9%20es%20la%20Clave%20Tributaria%3F%20Es%20la%20clave,con%20Clave%20%C3%9Anica%20del%20Estado%2C%20para%20realizar%20tr%C3%A1mites"
                  target="_blank"
                >
                  <TaxesKeyHelpIcon /> ¿Qué es Clave Tributaria?
                </TaxesKeyHelpLink>
              </TaxesKeyHelp>
            </TaxesKey>
          </Section>
        </>
      )}
    </Root>
  )
}

RegisterWizardBusinessSearch.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardBusinessSearch.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardBusinessSearch
